import {} from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { cn } from '@/lib/utils/utils';
import { formatRef } from '@/lib/utils/bible-utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { BookOpen } from 'lucide-react';
import { IciclePreviewLines } from './IciclePreviewLines';
import { nodeAccentColorFromPercent } from '@/lib/utils/color-utils';
import { useIciclePositioning } from '@/lib/canon-context/use-icicle-positioning';
import { isDescendantOf } from '@/lib/outline/outline-tree-utils';

interface IcicleChartProps {
    outline: Outline;
    focusedNode?: OutlineNode; // focus a parent subtree
    highlightedNode?: OutlineNode; // child containing the passage
    highlightedPassageId?: string; // to highlight the correct preview line
    isDarkMode?: boolean;
    onNodeClick?: (node: OutlineNode) => void;
    onZoomOut?: () => void;
    transitionNodeId?: string;
    onTransitionDone?: () => void;
}

function isLeafNode(node: OutlineNode): boolean {
    return !node.children || node.children.length === 0;
}

export function IcicleChart({ outline, focusedNode, highlightedNode, highlightedPassageId, isDarkMode, onNodeClick, onZoomOut, transitionNodeId, onTransitionDone }: IcicleChartProps) {
    const { nodesByColumn, column1Nodes, totalVerses } = useIciclePositioning(outline, focusedNode, highlightedNode);
    const isInFocus = (node: OutlineNode) => !focusedNode || isDescendantOf(node, focusedNode);

    return (
        <LayoutGroup id="icicle-chart">
            <div
                className="relative w-full h-full overflow-hidden flex gap-2 pb-4"
            >
                {Object.entries(nodesByColumn).map(([colIndex, nodes]) => {
                    const isRightCol = Number(colIndex) === 1;
                    return (
                    <motion.div key={colIndex} layout className="flex-1 min-w-[128px] flex flex-col h-full" data-icicle-col={colIndex}>
                        <AnimatePresence initial={false} mode="popLayout">
                            {nodes.map(p => (
                    <Tooltip key={p.node.id} delayDuration={250}>
                        <TooltipTrigger asChild>
                            <motion.button
                                layout
                                layoutId={transitionNodeId === p.node.id ? `rail-icicle-${p.node.id}` : p.node.id}
                                type="button"
                                aria-label={`${p.node.title || ''}`}
                                data-node-id={p.node.id}
                                className={cn(
                                    "relative m-[2px] rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60",
                                    "backdrop-blur-md overflow-hidden"
                                )}
                                style={{
                                    flexGrow: p.heightPercent,
                                    flexShrink: 0,
                                    flexBasis: `max(${p.heightPercent}%, 56px)`,
                                    minHeight: '56px',
                                    opacity: isInFocus(p.node) ? 1 : 0.35,
                                    backgroundColor: p.isHighlighted 
                                        ? 'rgba(255, 255, 255, 0.15)' 
                                        : 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    boxShadow: p.isHighlighted
                                        ? '0 0 0 2px rgba(250,204,21,0.9), 0 0 20px rgba(250,204,21,0.35), 0 4px 12px rgba(0,0,0,0.3)'
                                        : isDarkMode 
                                            ? '0 4px 12px rgba(0,0,0,0.3)' 
                                            : '0 4px 12px rgba(0,0,0,0.2)'
                                }}
                                animate={{ opacity: isInFocus(p.node) ? 1 : 0.35 }}
                                transition={{ 
                                    layout: { duration: 0.3, ease: "easeInOut" },
                                    opacity: { duration: 0.15 }
                                }}
                                whileHover={{ 
                                    scale: 1.02,
                                    boxShadow: p.isHighlighted
                                        ? '0 0 24px rgba(250,204,21,0.4), 0 6px 16px rgba(0,0,0,0.4)'
                                        : isDarkMode
                                            ? '0 6px 16px rgba(255,255,255,0.1)'
                                            : '0 6px 16px rgba(0,0,0,0.3)'
                                }}
                                onClick={(e) => {
                                    // Column 0 (left) triggers zoom out, other columns navigate to node
                                    if (Number(colIndex) === 0) {
                                        try {
                                            const el = e.currentTarget as HTMLElement;
                                            const srcRect = el.getBoundingClientRect();
                                            // Prefer highlighted child id for BookRail match if available
                                            const railId = highlightedNode?.id || p.node.id;
                                            const railEl = document.querySelector(`[data-rail="book"][data-node-id="${railId}"]`) as HTMLElement | null;
                                            console.log('[Context ZoomOut] source:left-cell', p.node.id, srcRect);
                                            if (railEl) console.log('[Context ZoomOut] matching-bookrail', railId, railEl.getBoundingClientRect());
                                            else console.log('[Context ZoomOut] matching-bookrail NOT FOUND for', railId);
                                        } catch {}
                                        onZoomOut?.();
                                        // Log target after layout settles (double RAF to allow paint)
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                try {
                                                    const targetId = highlightedNode?.id || p.node.id;
                                                    const leftTarget = document.querySelector(`[data-icicle-col="0"] [data-node-id="${targetId}"]`) as HTMLElement | null;
                                                    const rightTarget = document.querySelector(`[data-icicle-col="1"] [data-node-id="${targetId}"]`) as HTMLElement | null;
                                                    if (leftTarget) console.log('[Context ZoomOut] post-target:left-cell (2raf)', targetId, leftTarget.getBoundingClientRect());
                                                    if (rightTarget) console.log('[Context ZoomOut] post-target:right-cell (2raf)', targetId, rightTarget.getBoundingClientRect());
                                                    if (!leftTarget && !rightTarget) console.log('[Context ZoomOut] post-target not found in columns (2raf) for', targetId);
                                                } catch {}
                                            });
                                        });
                                    } else {
                                        onNodeClick?.(p.node);
                                    }
                                }}
                                onLayoutAnimationComplete={() => { if (transitionNodeId === p.node.id) onTransitionDone?.(); }}
                                {...(isRightCol ? { exit: { opacity: 0 } } : {})}
                            >
                                {/* Inner accent stripe tied to BookRail color */}
                                <div
                                    className="absolute left-0 top-0 bottom-0"
                                    style={{
                                        width: '4px',
                                        backgroundColor: nodeAccentColorFromPercent(p.absMidPercent, !!isDarkMode),
                                        opacity: 0.95
                                    }}
                                />
                                {/* Leaf node indicator */}
                                {isLeafNode(p.node) && (
                                    <div className="absolute top-1 right-1 bg-white/20 rounded p-1">
                                        <BookOpen className="w-3 h-3 md:w-4 md:h-4 text-white" />
                                    </div>
                                )}
                                
                                {/* Always show inline labels since we have min-height of 56px */}
                                <div className="absolute inset-2 text-sm md:text-2xl leading-tight text-white drop-shadow-md flex flex-col justify-center cursor-pointer">
                                    <div className="font-semibold line-clamp-2 md:truncate">{formatRef(p.node.ref)}</div>
                                    <div className="line-clamp-4 md:truncate opacity-90 text-xs md:text-lg">{p.node.title}</div>
                                </div>
                            </motion.button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                            <div className="flex flex-col gap-1">
                                <div className="font-bold">{outline.book} {formatRef(p.node.ref)}</div>
                                <div className="text-xs opacity-80">{p.node.title}</div>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                        ))}
                    </AnimatePresence>
                </motion.div>
            );})}
            
            {/* Preview lines column - shows children of nodes in column 1 */}
            <IciclePreviewLines
                column1Nodes={column1Nodes}
                highlightedPassageId={highlightedPassageId}
                isDarkMode={isDarkMode}
                onNodeClick={onNodeClick}
            />
            
        </div>
        </LayoutGroup>
    );
}
